import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { MDBox } from '../../../../Component/Common/Components/MDBox';
import { PageTitle } from '../../../../Component/Common/Components/PageTitle';
import { ScorecardAtom } from '../../../../State/Scorecard.Atom';
import { DeviationsService } from '../../../../Component/Api/EmploymentEquity/Deviations/DeviationsService';
import { DeviationsDto } from '../../../../Component/Model/Dto/EmploymentEquity/Deviations';
import { OccupationalLevelAccordion } from './OccupationalLevelAccordion';
import { DesignatedDisabledAccordion } from './DesignatedDisabledAccordion';
import { formatPeriodHeading } from './justificationsHelper';

export function JustificationsPage() {
    const scorecard = useRecoilValue(ScorecardAtom);

    const [planSummary, setPlanSummary] = useState<DeviationsDto.PlanSummaryDto | null>(null);
    const [justificationSummary, setJustificationSummary] = useState<DeviationsDto.DeviationsJustificationSummaryDto | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const refreshJustificationSummary = useCallback(() => {
        if (!scorecard.scorecardId) return;
        DeviationsService.GetJustificationSummary(scorecard.scorecardId)
            .then(res => setJustificationSummary(res.data))
            .catch(() => setJustificationSummary(null));
    }, [scorecard.scorecardId]);

    useEffect(() => {
        if (!scorecard.scorecardId) return;
        setLoading(true);
        setError(null);
        DeviationsService.GetPlanSummary(scorecard.scorecardId)
            .then(res => setPlanSummary(res.data))
            .catch(() => setError('Failed to load plan summary data.'))
            .finally(() => setLoading(false));
        refreshJustificationSummary();
    }, [scorecard.scorecardId, refreshJustificationSummary]);

    const periodHeading: string = useMemo(() => {
        if (!planSummary) return '';
        return formatPeriodHeading(planSummary.planYear, planSummary.reportingPeriodStart, planSummary.reportingPeriodEnd);
    }, [planSummary]);

    return (
        <MDBox style={{ padding: '10px 0' }}>
            <PageTitle title='Deviations - Justifications' />

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Box sx={{ textAlign: 'center', py: 4, color: 'error.main', fontSize: '0.875rem' }}>
                    {error}
                </Box>
            )}

            {!loading && !error && planSummary && planSummary.occupationalLevels.map(occupationalLevel => (
                <OccupationalLevelAccordion
                    key={occupationalLevel.occupationalLevelId}
                    scorecardId={scorecard.scorecardId}
                    occupationalLevel={occupationalLevel}
                    periodHeading={periodHeading}
                    justificationSummary={justificationSummary?.occupationalLevels.find(l => l.occupationalLevelId === occupationalLevel.occupationalLevelId) ?? null}
                    onJustificationsChanged={refreshJustificationSummary}
                />
            ))}

            {!loading && !error && planSummary && planSummary.designatedDisabled && (
                <DesignatedDisabledAccordion
                    designatedDisabled={planSummary.designatedDisabled}
                    periodHeading={periodHeading}
                    justificationSummary={justificationSummary?.designatedDisabled ?? null}
                    onJustificationsChanged={refreshJustificationSummary}
                />
            )}
        </MDBox>
    );
}
